import { createAllUsersQueryOptions } from '@/services/users/userProfile.options';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import DrawerHeader from '@/components/Organisms/BasicDrawer/DrawerHeader.component';
import { DrawerLayout } from '@/layouts/DrawerLayout';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import {
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControlLabel,
  List,
  ListItem,
  Stack,
  Theme,
  useMediaQuery,
  Switch,
} from '@mui/material';
import { Box, Card, Divider, Typography, AutoComplete } from '@utilisourcepackagelibdev/utilisourcepackagelib';
import TextBox from '@/components/Molecules/Textbox/Textbox.component';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import LoadingButton from '@mui/lab/LoadingButton';
import ConfirmModal from '@/components/Molecules/Modal/ConfirmModal.component';
import { useQuery } from '@tanstack/react-query';
import { createCustomerOptionsQuery } from '@/services/customer/customer.options';
import useTicketTypes from '@/services/type/type.options';
import { user, UsersSchema, PasswordSchema } from '@/interfaces/schemas/users.schema';
import CheckboxInput from '@/components/Atoms/Checkbox/CheckboxInput.component';
import { CustomerEntity } from '@/interfaces/customer.interface';
import { TicketType } from '@/interfaces/ticketType.interface';
import { createRolesQueryOptions } from '@/services/rolesPermissions/rolesPermission.options';
import FailedToArchiveModal from './UsersAdminFailedToArchiveModal.component';
import { useTheme } from '@mui/material/styles';
import useUsers from '@/services/users/users.mutation';
import dayjs from 'dayjs';
import RestoreIcon from '@mui/icons-material/Restore';
import PasswordForm from './usersAdminPasswordForm.component';

const UserDrawer = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false);
  const [openFailedToDeleteModal, setOpenFailedToDeleteModal] = useState<boolean>(false);
  const [isResoringArchiving, setIsResoringArchiving] = useState<boolean>(false);
  const [ticketData, setTicketData] = useState<any>(null);
  const [workflowData, setWorkflowData] = useState<any>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const params = useParams({ from: '/_auth/users/$userId' });
  const { variant } = useSearch({ from: '/_auth/users/$userId' });
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const theme = useTheme();

  const { executeUserAction, executeUserActionMutation } = useUsers();
  const rolesPermissions = useQuery(createRolesQueryOptions());

  const allUsers = useQuery(createAllUsersQueryOptions());
  const customers = useQuery(createCustomerOptionsQuery());
  const { ticketTypesByAllCustomers } = useTicketTypes();
  const customerData = customers?.data;
  const ticketTypeData = ticketTypesByAllCustomers?.data;
  const rolesData = rolesPermissions?.data;
  const canSend = true;
  const navigate = useNavigate();
  const currentUser = allUsers?.data?.find((user: any) => user.agentid === params.userId);

  useEffect(() => {
    setIsOpen(true);
  }, []);

  // user form
  const methods = useForm<z.infer<typeof UsersSchema>>({
    mode: 'all',
    defaultValues: user,
    resolver: zodResolver(UsersSchema),
  });
  const { control, setValue, watch } = methods;

  useEffect(() => {
    if (currentUser) {
      methods.reset({
        AgentType: currentUser.AgentType || 0,
        agentid: currentUser.agentid || 0,
        CustomerList: currentUser.CustomerList ? currentUser.CustomerList : [],
        TypesList: currentUser.TypesList ? currentUser.TypesList : [],
        DisabledBy: currentUser.DisabledBy || 0,
        DisabledDate: currentUser.DisabledDate || '0001-01-01T00:00:00Z',
        FilterFeatureOn: currentUser.FilterFeatureOn || false,
        TimeZone: currentUser.TimeZone || '',
        TimeZoneOffset: currentUser.TimeZoneOffset || '',
        archivetime: currentUser.archivetime || '0001-01-01T00:00:00Z',
        customer: currentUser.customer || 0,
        eclassid: currentUser.eclassid || 0,
        email: currentUser.email || '',
        flags: currentUser.flags || 0,
        name: currentUser.name || '',
        phone: currentUser.phone || '',
        roleid: currentUser.roleid || 0,
        rolename: currentUser.rolename || '',
      });
    }
  }, [currentUser]);

  const customerList = watch('CustomerList');
  const typeList = watch('TypesList');
  const roleName = watch('rolename');
  const roleId = watch('roleid');
  const name = watch('name');
  const filterFeatureOn = watch('FilterFeatureOn');

  const handleClose = () => {
    setIsOpen(false);
    navigate({ to: '/users' });
  };

  // if customer: 0, endpoint will assign the customer to whoever is creating the user


  const handleSubmit: SubmitHandler<any> = (formValues: any) => {
    setIsSaving(true);
    if (variant === 'edit') {
      executeUserAction({
        actionType: 'updateUser',
        payload: {
          Agent: {
            AgentID: currentUser?.agentid,
            Name: formValues.name,
            Flags: 0,
            Email: formValues.email,
            Phone: formValues.phone,
            EClassID: 0,
            Customer: 0,
            Roleoverride: roleId, // Roleoverride === roleId,
            AgentType: 1,
            FilterFeatureOn: filterFeatureOn,
            TimeZoneOffSet: '',
            TimeZone: '',
          },
          AgentTypes: typeList?.map((type: any) => type?.TypeID) || [],
          AgentCustomers: customerList?.map((customer: any) => customer?.CustomerID) || [],
        },
      });
    } else if (variant === 'new') {
      executeUserAction({
        actionType: 'createNewUser',
        payload: {
          Agent: {
            AgentID: 0,
            Name: formValues.name,
            Flags: 0,
            Email: formValues.email,
            Phone: formValues.phone,
            EClassID: 0,
            Roleoverride: roleId,
            // sending 0 will assign the agent to the user's customer 
            Customer: 0,
            AgentType: 1,
            FilterFeatureOn: filterFeatureOn,
            TimeZoneOffSet: '',
            TimeZone: '',
          },
          AgentTypes: typeList?.map((type: any) => type?.TypeID) || [],
          AgentCustomers: customerList?.map((customer: any) => customer?.CustomerID) || [],
        },
      });
    }
    console.log('setSaving false')
    setIsSaving(false);
  };

  const handleArchiveRestore = () => {
    if (currentUser?.archivetime === '0001-01-01T00:00:00Z') {
      executeUserActionMutation.mutate(
        {
          actionType: 'archiveUser',
          payload: {
            agentId: currentUser?.agentid,
          },
        },
        {
          onSuccess: (response: any) => {
            if (response?.data?.canArchive === false) {
              setTicketData(response?.data?.assignedTickets);
              setWorkflowData(response?.data?.activeWorkflowRules);
              setOpenFailedToDeleteModal(true);
            }
          },
          onError: (error: any) => {
            setIsResoringArchiving(false);
          },
        },
      );
    } else {
      executeUserActionMutation.mutate({
        actionType: 'restoreUser',
        payload: {
          agentId: currentUser?.agentid,
        },
      });
    }
  };

  // Calculate indeterminate state based on actual customerList data
  const totalCustomers = customerData?.length || 0;
  const selectedCustomers = customerList?.length || 0;
  const customerIndeterminate = selectedCustomers > 0 && selectedCustomers < totalCustomers;
  const checkAllCustomers = selectedCustomers === totalCustomers && totalCustomers > 0;

  // Calculate ticket type states similarly
  const totalTicketTypes = ticketTypeData?.length || 0;
  const selectedTicketTypes = typeList?.length || 0;
  const ticketTypeIndeterminate = selectedTicketTypes > 0 && selectedTicketTypes < totalTicketTypes;
  const checkAllTicketTypes = selectedTicketTypes === totalTicketTypes && totalTicketTypes > 0;

  return (
    <DrawerLayout
      open={isOpen}
      onClose={handleClose}
      anchor="right"
      keepMounted
      width={'60%'}
      Header={<DrawerHeader close={handleClose} title={variant === 'edit' ? 'Edit User' : 'New User'} />}>
      {allUsers?.isPending || allUsers?.isLoading ? (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}>
          <CircularProgress />
        </Box>
      ) : (
        <FormProvider {...methods}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Divider sx={{ mt: 3 }} />
            {currentUser?.agentid && (
              <Typography
                variant="h6"
                sx={{ color: 'grey', alignSelf: 'flex-end', width: '100%', textAlign: 'right', pr: 4 }}>
                {currentUser?.agentid + '-' + currentUser?.customer}
              </Typography>
            )}
            {currentUser && currentUser?.archivetime !== '0001-01-01T00:00:00Z' && (
              <Chip
                color="error"
                label={`This user was archived on ${dayjs(currentUser?.archivetime).format('MM/DD/YYYY')}.`}
              />
            )}
            <Box sx={{ flex: 1, overflow: 'auto', px: 2 }}>
              {/* @ts-ignore */}
              <Container
                maxWidth="md"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 5,
                  py: 5,
                }}
                component="form"
                onSubmit={methods.handleSubmit(handleSubmit)}>
                <Card sx={{ p: 4 }}>
                  <Stack direction="column" spacing={4}>
                    <Stack direction={isDesktop ? 'row' : 'column'} spacing={4}>
                      <TextBox size="small" id="name" name="name" label="Name" control={control} fullWidth />
                      <Box sx={{ width: '100%' }}>
                        <AutoComplete
                          id="rolename"
                          control={control}
                          options={rolesData?.map((role: any) => ({
                            label: role.rolename,
                            id: role.roleid,
                          }))}
                          label="Role"
                          multiple={false}
                          autocompleteProps={{
                            size: 'small',
                            id: 'type',
                            onChange: (_, selectedOption) => {
                              setValue('rolename', selectedOption?.label ?? '');
                              setValue('roleid', selectedOption?.id ?? 0);
                            },
                          }}
                        />
                      </Box>
                    </Stack>
                    <Stack direction={isDesktop ? 'row' : 'column'} spacing={4}>
                      <TextBox size="small" id="phone" name="phone" label="Phone" control={control} fullWidth />
                      <TextBox size="small" id="email" name="email" label="Email" control={control} fullWidth />
                    </Stack>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={filterFeatureOn}
                          onChange={() => setValue('FilterFeatureOn', !filterFeatureOn)}
                          size="small"
                        />
                      }
                      label="Filter Feature On"
                      sx={{ p: 1, m: 1 }}
                    />
                  </Stack>
                </Card>
                <Stack direction={isDesktop ? 'row' : 'column'} spacing={4}>
                  <Card sx={{ flex: 1, p: 5 }}>
                    <Stack direction={isDesktop ? 'row' : 'column'} spacing={5} alignItems="center">
                      <Typography variant="h6">Linked Customers</Typography>
                      {variant === 'edit' && (customerList === null || customerList.length === 0) && (
                        <Chip color="primary" variant="outlined" label="No customers linked to this user" />
                      )}
                    </Stack>

                    <List>
                      <ListItem sx={{ p: 0, m: 0 }}>
                        <CheckboxInput
                          label="All"
                          sxProps={{ p: 0, m: 0 }}
                          checked={checkAllCustomers}
                          id="checkAllCustomers"
                          indeterminate={customerIndeterminate}
                          onDataChange={() => {
                            if (checkAllCustomers) {
                              // If all are checked, clear everything
                              setValue('CustomerList', []);
                            } else {
                              // If none or some are checked, select all
                              const newCustomerList = customerData?.map((customer: CustomerEntity) => ({
                                CustomerID: customer.CustomerEntId,
                                CustomerName: customer.EntName,
                              }));
                              setValue('CustomerList', newCustomerList || []);
                            }
                          }}
                        />
                      </ListItem>
                      {customerData?.map((customer: CustomerEntity) => {
                        const isChecked = customerList?.some((c: any) => c.CustomerID === customer.CustomerEntId);
                        return (
                          <ListItem sx={{ p: 0, m: 0 }} key={customer.CustomerEntId}>
                            <CheckboxInput
                              sxProps={{ p: 1, m: 0 }}
                              id={customer.EntName}
                              label={customer.EntName}
                              checked={isChecked || false}
                              onDataChange={() => {
                                if (isChecked) {
                                  const filteredCustomerList =
                                    customerList?.filter((c: any) => c.CustomerID !== customer.CustomerEntId) || [];
                                  setValue('CustomerList', filteredCustomerList);
                                } else {
                                  const newCustomer = {
                                    CustomerID: customer.CustomerEntId,
                                    CustomerName: customer.EntName,
                                  };
                                  setValue('CustomerList', [...(customerList || []), newCustomer]);
                                }
                              }}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </Card>
                  <Card sx={{ flex: 1, p: 5 }}>
                    <Stack direction={isDesktop ? 'row' : 'column'} spacing={5} alignItems="center">
                      <Typography variant="h6">Ticket Types</Typography>
                      {variant === 'edit' && (typeList === null || typeList.length === 0) && (
                        <Chip color="primary" variant="outlined" label="No ticket types linked to this user" />
                      )}
                    </Stack>

                    <List>
                      <ListItem sx={{ p: 0, m: 0 }}>
                        <CheckboxInput
                          label="All"
                          sxProps={{ p: 0, m: 0 }}
                          checked={checkAllTicketTypes}
                          id="checkAllTicketTypes"
                          indeterminate={ticketTypeIndeterminate}
                          onDataChange={() => {
                            if (checkAllTicketTypes) {
                              // If all are checked, clear everything
                              setValue('TypesList', []);
                            } else {
                              // If none or some are checked, select all
                              const newTicketTypeList = ticketTypeData?.map((ticketType: TicketType) => ({
                                TypeID: ticketType.TypeID,
                                TypeName: ticketType.TypeName,
                              }));
                              setValue('TypesList', newTicketTypeList || []);
                            }
                          }}
                        />
                      </ListItem>
                      {ticketTypeData?.map((ticketType: TicketType) => {
                        const isChecked = typeList?.some((t: any) => t.TypeID === ticketType.TypeID);
                        return (
                          <ListItem sx={{ p: 0, m: 0 }} key={ticketType.TypeID}>
                            <CheckboxInput
                              sxProps={{ p: 1, m: 0 }}
                              id={ticketType.TypeName}
                              label={ticketType.TypeName}
                              checked={isChecked || false}
                              onDataChange={() => {
                                if (isChecked) {
                                  const filteredTicketTypeList =
                                    typeList?.filter((t: any) => t.TypeID !== ticketType.TypeID) || [];
                                  setValue('TypesList', filteredTicketTypeList);
                                } else {
                                  const newType = {
                                    TypeID: ticketType.TypeID,
                                    TypeName: ticketType.TypeName,
                                  };
                                  setValue('TypesList', [...(typeList || []), newType]);
                                }
                              }}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </Card>
                </Stack>
                <PasswordForm currentUser={currentUser} />
              </Container>
            </Box>
            <Box sx={{ position: 'sticky', bottom: 0, borderTop: 1, borderColor: 'divider' }}>
              <Box
                sx={{
                  p: 5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '1rem',
                }}>
                {/* <LoadingButton
                  size="small"
                  loading={false}
                  variant="text"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setOpenConfirmModal(true)}>
                  Delete
                </LoadingButton> */}

                {currentUser && currentUser?.archivetime !== '0001-01-01T00:00:00Z' ? (
                  <LoadingButton
                    loading={isResoringArchiving}
                    color="error"
                    startIcon={<RestoreIcon />}
                    onClick={() => {
                      setIsResoringArchiving(true);
                      setOpenConfirmModal(true);
                    }}>
                    Restore
                  </LoadingButton>
                ) : (
                  <LoadingButton
                    loading={isResoringArchiving}
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => {
                      setIsResoringArchiving(true);
                      setOpenConfirmModal(true);
                    }}>
                    Archive
                  </LoadingButton>
                )}
                {openConfirmModal && (
                  <ConfirmModal
                    open={openConfirmModal}
                    setOpen={setOpenConfirmModal}
                    title="Are you sure?"
                    content={
                      currentUser?.archivetime === '0001-01-01T00:00:00Z'
                        ? 'This action will delete this user and all associated data.'
                        : 'This action will restore this user and all associated data.'
                    }
                    onSuccess={handleArchiveRestore}
                    onClose={() => setOpenConfirmModal(false)}
                    cancelButtonText="Cancel"
                    confirmButtonText={currentUser?.archivetime === '0001-01-01T00:00:00Z' ? 'Archive' : 'Restore'}
                  />
                )}
                {openFailedToDeleteModal && (
                  <FailedToArchiveModal
                    open={openFailedToDeleteModal}
                    setOpen={setOpenFailedToDeleteModal}
                    content="This action will permanently delete the status."
                    onSuccess={handleArchiveRestore}
                    onClose={() => {
                      setOpenFailedToDeleteModal(false);
                      setIsResoringArchiving(false);
                    }}
                    ticketData={ticketData}
                    workflowData={workflowData}
                    name={name}
                  />
                )}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setTimeout(() => {
                        navigate({ to: '/users' });
                      }, 300);
                    }}>
                    Cancel & Close
                  </Button>
                  <LoadingButton
                    loading={isSaving}
                    disabled={!canSend}
                    variant="contained"
                    onClick={() => {
                      methods.handleSubmit((data) => {
                        handleSubmit(data);
                      })();
                    }}>
                    Save
                  </LoadingButton>
                </Box>
              </Box>
            </Box>
          </Box>
        </FormProvider>
      )}
    </DrawerLayout>
  );
};

export default UserDrawer;
